import { useQuery } from "@tanstack/react-query";
import { Music, Home } from "lucide-react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import JamTracker from "@/pages/jam-tracker";
import OpenMicTracker from "@/pages/open-mic-tracker";
import GenericEventTracker from "@/pages/generic-event-tracker";
import ParticipantDetail from "@/pages/participant-detail";
import FocusedView from "@/pages/focused-view";
import type { JamNight } from "@shared/schema";

interface EventRouterProps {
  eventId: number;
  activeTab?: string;
}

export default function EventRouter({ eventId, activeTab }: EventRouterProps) {
  const [location] = useLocation();
  const { data: event, isLoading } = useQuery<JamNight>({
    queryKey: [`/api/events/${eventId}`],
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Music className="h-8 w-8 animate-pulse mx-auto mb-4 text-primary" />
          <p className="text-slate-600">Loading event data...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Event Not Found</h1>
          <p className="text-slate-600 mb-4">The event you're looking for doesn't exist.</p>
          <Button onClick={() => window.location.href = '/'} className="inline-flex items-center gap-2">
            <Home className="w-4 h-4" />
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  // Check if we're on the participants page
  if (location === `/event/${eventId}/participants`) {
    return <ParticipantDetail jamNightId={eventId} />;
  }

  // Check if we're on the focused view page
  if (location.startsWith(`/event/${eventId}/focused`)) {
    // Extract view type from URL if present
    const viewType = location.split('/').pop();
    const validViewType = viewType !== 'focused' ? viewType : undefined;
    return <FocusedView jamNightId={eventId} viewType={validViewType} />;
  }

  // Default to the tracker pages
  if (event.type === "openmic") {
    return <OpenMicTracker jamNightId={eventId} activeTab={activeTab} />;
  } else if (event.type === "generic") {
    return <GenericEventTracker jamNightId={eventId} activeTab={activeTab} />;
  } else {
    return <JamTracker jamNightId={eventId} activeTab={activeTab} />;
  }
}